import { useEffect, useState } from "react";
import useSWRSubscription from "swr/subscription";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { firestore } from "../../../configs/FirebaseConfig";
import { FIRESTORE_ORDERPICKINGS_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../constants/CompanyConstants";

export const useOrderPickings = (pickingDate = null) => {
  const [orderPickings, setOrderPickings] = useState([]);

  const {
    data,
    error: swrError,
    isLoading,
    isValidating,
  } = useSWRSubscription(
    pickingDate ? `orderPickings-${pickingDate}` : "orderPickings",
    (key, { next }) => {
      const orderPickingsRef = collection(
        firestore,
        FIRESTORE_REGIONS_TABLE,
        COMPANY_COUNTRY_CODE,
        FIRESTORE_ORDERPICKINGS_TABLE,
      );

      // Filter by pickingDate if provided, otherwise fetch all
      const orderPickingsQuery = pickingDate
        ? query(orderPickingsRef, where("pickingDate", "==", pickingDate))
        : orderPickingsRef;

      const unsubscribe = onSnapshot(
        orderPickingsQuery,
        (snapshot) => {
          const orderPickingsData = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          next(null, orderPickingsData);
        },
        (error) => {
          console.error("Error listening to order pickings:", error);
          next(error, null);
        },
      );

      return () => unsubscribe();
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
    },
  );

  useEffect(() => {
    if (data) {
      setOrderPickings(data);
    }
  }, [data]);

  // Helper functions
  const getOrderPickingById = (id) => {
    return orderPickings.find((picking) => picking.id === id);
  };

  const getOrderPickingsByStatus = (status) => {
    return orderPickings.filter((picking) => picking.status === status);
  };

  const getOrderPickingsByDate = (date) => {
    return orderPickings.filter((picking) => picking.pickingDate === date);
  };

  const getOrderPickingsByDateRange = (startDate, endDate) => {
    return orderPickings.filter((picking) => {
      const pickingDate = new Date(picking.pickingDate);
      const start = new Date(startDate);
      const end = new Date(endDate);
      return pickingDate >= start && pickingDate <= end;
    });
  };

  const searchOrderPickings = (searchTerm) => {
    const term = searchTerm.toLowerCase();
    return orderPickings.filter(
      (picking) =>
        picking.id?.toLowerCase().includes(term) ||
        picking.pickingNumber?.toLowerCase().includes(term) ||
        picking.customerName?.toLowerCase().includes(term) ||
        picking.notes?.toLowerCase().includes(term),
    );
  };

  const getActiveOrderPickings = () => {
    return orderPickings.filter((picking) => picking.status !== "completed" && picking.status !== "cancelled");
  };

  const getCompletedOrderPickings = () => {
    return orderPickings.filter((picking) => picking.status === "completed");
  };

  return {
    data: orderPickings,
    orderPickings,
    loading: isLoading,
    isLoading,
    isValidating,
    error: swrError,
    // Helper functions
    getOrderPickingById,
    getOrderPickingsByStatus,
    getOrderPickingsByDate,
    getOrderPickingsByDateRange,
    searchOrderPickings,
    getActiveOrderPickings,
    getCompletedOrderPickings,
  };
};
